// Every font used on the members site is configured here: font families, font
// weights, font sizes (Fonts roles) and text colours (FontColors). Change a value
// here and every page that uses it updates.
//
// JS styles (sx, style, makeStyles) use the objects directly:
//   sx={{ ...Fonts.GeneralHeaderFont }}   or   fontSize: Fonts.GeneralHeaderFont.fontSize
// CSS/SCSS and styled-components use the CSS variables generated from this file
// (see fontVariables.js):
//   font-size: var(--general-header-font-size);
//   font-weight: var(--general-header-font-weight);
//   font-family: var(--general-header-font-family);
//
// fontSize: a number is px, a string is any CSS size, an object is responsive
// by MUI breakpoint ({ xs, sm, md, lg, xl }).
//
// Weights and colours on their own:
//   JS:   fontWeight: FontWeights.SemiBold      color: FontColors.Gray4F4F4F
//   CSS:  font-weight: var(--font-weight-semi-bold);
//         color: var(--font-color-gray4f4f4f);
// Colour names are <colour family><hex>; "Alpha<n>" marks n% opacity.

// Same font as the admin app (Roboto); loaded in public/index.html.
const PrimaryFontFamily = "Roboto, Helvetica, Arial, sans-serif";

export const FontFamilies = {
  General: PrimaryFontFamily,
  Monospace: "monospace",
  MathSymbol: "math",
  SansSerif: "sans-serif",
  LoadingScreen: PrimaryFontFamily,
  MaterialDefault: PrimaryFontFamily,
  SearchSelect: PrimaryFontFamily,
  SearchSelectInput: PrimaryFontFamily,
};

export const GeneralFontFamily = FontFamilies.General;

export const FontWeights = {
  Light: 300,
  Regular: 400,
  Medium: 500,
  SemiBold: 600,
  Bold: 700,
};

const { Regular, Medium, SemiBold, Bold } = FontWeights;

export const Fonts = {
  // Headings
  GeneralDisplayFont: { fontFamily: GeneralFontFamily, fontSize: 32, fontWeight: Bold },
  GeneralPageHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 26, fontWeight: Bold },
  GeneralHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 20, fontWeight: Bold },
  GeneralSubHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 16, fontWeight: SemiBold },
  GeneralSmallHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: SemiBold },

  // Body text
  GeneralXLargeBodyFont: { fontFamily: GeneralFontFamily, fontSize: 18, fontWeight: Regular },
  GeneralLargeBodyFont: { fontFamily: GeneralFontFamily, fontSize: 16, fontWeight: Regular },
  GeneralBodyFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: Regular },
  GeneralSmallBodyFont: { fontFamily: GeneralFontFamily, fontSize: 13, fontWeight: Regular },
  GeneralCaptionFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: Regular },
  GeneralTinyFont: { fontFamily: GeneralFontFamily, fontSize: 10, fontWeight: Regular },

  // Components
  GeneralLabelFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: Medium },
  GeneralSmallLabelFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: Medium },
  GeneralButtonFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: SemiBold },
  GeneralSmallButtonFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: SemiBold },
  GeneralInputFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: Regular },
  GeneralCheckBoxFont: { fontFamily: GeneralFontFamily, fontSize: 22, fontWeight: Regular },
  GeneralTableHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: Bold },
  GeneralTabFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: Medium },
  GeneralBadgeFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: Medium },
  GeneralNavFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: Medium },
  GeneralErrorFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: Regular },
  GeneralAmountFont: { fontFamily: GeneralFontFamily, fontSize: 14, fontWeight: SemiBold },
  GeneralLargeAmountFont: { fontFamily: GeneralFontFamily, fontSize: 20, fontWeight: Bold },

  // Sidebar menu
  PortalMenuItemFont: { fontFamily: GeneralFontFamily, fontSize: 16, fontWeight: Regular },
  PortalMenuSectionFont: { fontFamily: GeneralFontFamily, fontSize: 12, fontWeight: SemiBold },

  // Page-specific
  MembersPageHeaderFont: { fontFamily: GeneralFontFamily, fontSize: { xs: 18, sm: 22, md: 28 }, fontWeight: Bold },
  MembersPageHeadlineFont: { fontFamily: GeneralFontFamily, fontSize: { xs: 22, sm: 26, lg: 32 }, fontWeight: Bold },
  PaymentResultPageHeaderFont: { fontFamily: GeneralFontFamily, fontSize: 36, fontWeight: SemiBold },
  ComingSoonPageTitleFont: { fontFamily: GeneralFontFamily, fontSize: 41, fontWeight: Regular },
  ComingSoonPageHeroFont: { fontFamily: GeneralFontFamily, fontSize: 50, fontWeight: Bold },
  ReligiousSchoolAboutHeaderFont: { fontFamily: GeneralFontFamily, fontSize: "1.75rem", fontWeight: Bold },
  ReligiousSchoolAboutBodyFont: { fontFamily: GeneralFontFamily, fontSize: "1.5625rem", fontWeight: Regular },
  FundraisingHeroTitleFont: { fontFamily: GeneralFontFamily, fontSize: "clamp(1.8rem, 4vw, 3rem)", fontWeight: Bold },
  FundraisingTitleFont: { fontFamily: GeneralFontFamily, fontSize: "clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight: Bold },
  FundraisingSubtitleFont: { fontFamily: GeneralFontFamily, fontSize: "clamp(.9rem, 1.8vw, 1.1rem)", fontWeight: Medium },
};

export const FontColors = {
  // Header text of every table (MUI tables and data grids)
  TableHeader: "#334155",

  // White, black, transparent
  White: "#ffffff",
  Black: "#000000",
  BlackAlpha54: "rgba(0, 0, 0, 0.54)",
  BlackAlpha87: "rgba(0, 0, 0, 0.87)",
  WhiteAlpha85: "rgba(255, 255, 255, 0.85)",
  WhiteAlpha50: "rgba(255, 255, 255, 0.5)",
  WhiteAlpha80: "rgba(255, 255, 255, 0.8)",
  WhiteAlpha75: "rgba(255, 255, 255, 0.75)",
  WhiteAlpha45: "rgba(255, 255, 255, 0.45)",
  WhiteAlpha65: "rgba(255, 255, 255, 0.65)",
  BlackAlpha45: "rgba(0, 0, 0, 0.45)",

  // Grays
  GrayB5B5C3: "#b5b5c3",
  GrayACACAC: "#acacac",
  Gray4F4F4F: "#4f4f4f",
  Gray: "#808080",
  Gray575757: "#575757",
  GrayE7E7EB: "#e7e7eb",
  Gray8E8E8E: "#8e8e8e",
  Gray7B7B8B: "#7b7b8b",
  Gray666667: "#666667",
  Gray555555: "#555555",
  Gray212529: "#212529",
  GrayB6B6C4: "#b6b6c4",
  Gray9C9C9C: "#9c9c9c",
  GrayA4A4A4: "#a4a4a4",
  GrayD1D5DB: "#d1d5db",
  Gray9E9E9E: "#9e9e9e",
  GrayAAAAAA: "#aaaaaa",
  Gray999999: "#999999",
  Gray6C6D6D: "#6c6d6d",
  GrayF3F6F9: "#f3f6f9",
  Gray222222: "#222222",
  Gray888888: "#888888",
  Gray686869: "#686869",
  Gray343434: "#343434",
  Gray9FA0A2: "#9fa0a2",
  Gray4F4F4FAlpha60: "rgba(79, 79, 79, 0.6)",
  Gray2D2D2D: "#2d2d2d",
  GrayBEBECB: "#bebecb",
  GrayB5B5C4: "#b5b5c4",
  Gray777777: "#777777",
  Gray666666: "#666666",

  // Slate (blue-tinted grays)
  Slate3F4255: "#3f4255",
  Slate4D5565: "#4d5565",
  Slate64748B: "#64748b",
  Slate0F172A: "#0f172a",
  Slate3F4254: "#3f4254",
  Slate94A3B8: "#94a3b8",
  Slate404253: "#404253",
  Slate6B7280: "#6b7280",
  Slate464E5F: "#464e5f",
  Slate334155: "#334155",
  Slate475569: "#475569",
  SlateCBD5E1: "#cbd5e1",
  Slate9CA3AF: "#9ca3af",
  Slate344054: "#344054",
  Slate1A1F36: "#1a1f36",
  Slate5C6373: "#5c6373",
  Slate111827: "#111827",
  Slate3C4467: "#3c4467",
  Slate1F2937: "#1f2937",
  Slate374151: "#374151",
  Slate99ABBE: "#99abbe",
  Slate424770: "#424770",
  SlateAAB7C4: "#aab7c4",
  Slate1E293B: "#1e293b",
  Slate4B5563: "#4b5563",
  Slate555C6C: "#555c6c",
  Slate5C6473: "#5c6473",
  Slate284C58: "#284c58",

  // Blues
  Blue002E5C: "#002e5c",
  Blue2555EF: "#2555ef",
  Blue2455EF: "#2455ef",
  Blue007BFF: "#007bff",
  Blue1976D2: "#1976d2",
  Blue5B7792: "#5b7792",
  Blue70A0EE: "#70a0ee",
  Blue3764EF: "#3764ef",
  Blue1E3A5F: "#1e3a5f",
  Blue0C2E5C: "#0c2e5c",
  Blue3561F0: "#3561f0",
  Blue1675E0: "#1675e0",
  Blue155EEF: "#155eef",
  Blue6366F1: "#6366f1",
  Blue012B57: "#012b57",
  Blue1E46D0: "#1e46d0",
  Blue3C4DE7: "#3c4de7",
  BlueBFDBFE: "#bfdbfe",
  Blue2D5BF0: "#2d5bf0",
  Blue: "#0000ff",
  Blue3F69F0: "#3f69f0",
  Blue1E3A8A: "#1e3a8a",

  // Teals
  Teal1BC5BD: "#1bc5bd",

  // Greens
  Green81DC91: "#81dc91",
  Green4CAF50: "#4caf50",
  Green16A34A: "#16a34a",
  Green: "#008000",
  Green059669: "#059669",

  // Oranges
  OrangeFBA12C: "#fba12c",
  OrangeD97706: "#d97706",

  // Reds
  Red: "#ff0000",
  RedEF4444: "#ef4444",
  RedFA3372: "#fa3372",
  RedE53E3E: "#e53e3e",
  RedF1585E: "#f1585e",
  Red9E2146: "#9e2146",
  RedF44336: "#f44336",
  RedF25F66: "#f25f66",
  RedDC2626: "#dc2626",
  RedF2676D: "#f2676d",
  RedD32F2F: "#d32f2f",

  // Purples
  Purple7C3AED: "#7c3aed",
};
